/**
 * Page scroll lock shared by every overlay.
 *
 * Each overlay used to save `body.style.overflow`, set it to hidden, and put
 * the saved value back on close. Two overlays that overlap (the OTP security
 * ceremony and the react-bootstrap code dialog, which does the same thing)
 * then restore each other's 'hidden' when they close in the "wrong" order,
 * and the next screen — often My Wallet after a withdraw — cannot scroll at
 * all, on phone and computer alike.
 *
 * Now: overlays register a holder; scroll is unlocked when the last holder
 * leaves and no dialog is on screen. A watchdog clears any lock left behind
 * by something else (a dialog's own restore, a header menu that navigated
 * away while open).
 */

const holders = new Set();

/** Is anything on screen that is entitled to keep the page locked? */
const somethingOpen = () =>
  holders.size > 0 ||
  Boolean(document.querySelector('.modal.show, .offcanvas.show, .sec-ceremony, [data-scroll-lock]'));

const isLocked = (body) =>
  body.style.overflow === 'hidden' ||
  body.classList.contains('modal-open') ||
  [...body.classList].some((c) => c.includes('overflow_hide'));

function unlockBody() {
  const body = document.body;
  body.style.overflow = '';
  body.style.paddingRight = '';
  body.classList.remove('modal-open');
  // The public header's mobile menu class (CSS module, so the name is hashed).
  [...body.classList].filter((c) => c.includes('overflow_hide')).forEach((c) => body.classList.remove(c));
}

export function lockScroll(key) {
  holders.add(key);
  document.body.style.overflow = 'hidden';
}

export function unlockScroll(key) {
  holders.delete(key);
  if (!somethingOpen()) unlockBody();
}

/** Clear a lock nobody is holding any more. Safe to call any time. */
export function clearStaleScrollLock() {
  if (isLocked(document.body) && !somethingOpen()) unlockBody();
}

let installed = false;

/**
 * Watch the body for a lock appearing or being restored, and clear it once
 * nothing on screen needs it. The short delay lets a dialog finish opening or
 * closing (its fade is ~300 ms) before we decide.
 */
export function installScrollLockWatchdog() {
  if (installed || typeof MutationObserver === 'undefined') return;
  installed = true;
  let timer = null;
  const check = () => {
    clearTimeout(timer);
    timer = setTimeout(clearStaleScrollLock, 450);
  };
  new MutationObserver(check).observe(document.body, {
    attributes: true,
    attributeFilter: ['style', 'class'],
    childList: true,
  });
  window.addEventListener('popstate', check);
  window.addEventListener('hashchange', check);
}
